import { useEffect } from 'react'
import { useT } from '@/i18n/translations'

interface ThankYouSplashProps {
  language: string
  isDemoMode: boolean
  // When the interview was ended manually (abort) rather than finished
  // naturally: shows neutral "interview ended" copy instead of the
  // celebratory success message and skips the auto-redirect.
  aborted?: boolean
  // Post-interview redirect target from the interview template (public flow).
  // Falls back to the default when null/absent (e.g. the candidate flow).
  redirectUrl?: string | null
  onHome?: () => void
}

const DEFAULT_REDIRECT_URL = 'https://www.tlou.ai'
const REDIRECT_DELAY = 4000

export function ThankYouSplash({ language, isDemoMode, aborted = false, redirectUrl, onHome }: ThankYouSplashProps) {
  const t = useT(language)
  const target = redirectUrl || DEFAULT_REDIRECT_URL

  useEffect(() => {
    // Don't whisk the candidate away after an abort — let them read the
    // "progress not saved" note and choose to leave themselves.
    if (aborted) return
    const timer = setTimeout(() => {
      window.location.href = target
    }, REDIRECT_DELAY)
    return () => clearTimeout(timer)
  }, [aborted, target])

  const title = aborted
    ? t('interviewAbortedTitle')
    : isDemoMode ? t('demoCompleted') : t('interviewCompleted')
  const subtitle = aborted ? t('interviewAbortedMessage') : t('thankYouVisitUs')

  return (
    <div className="fixed inset-0 z-50 bg-brand-gradient flex flex-col items-center justify-center text-center px-4">
      {aborted ? (
        <svg
          className="w-16 h-16 mb-6 text-white/90"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="12" cy="12" r="9" />
          <path strokeLinecap="round" d="M9 9l6 6M15 9l-6 6" />
        </svg>
      ) : (
        <div className="text-6xl mb-6">🎉</div>
      )}
      <h1 className="text-3xl font-bold text-white mb-3">{title}</h1>
      <p className="text-lg text-white/80 max-w-md">{subtitle}</p>
      {!aborted && (
        <p className="text-sm text-white/50 mt-6">Redirecting in {REDIRECT_DELAY / 1000}s…</p>
      )}
      {onHome && (
        <button className="back-btn back-btn-home" onClick={onHome}>
          {language === 'de' ? '← Startseite' : '← Home'}
        </button>
      )}
    </div>
  )
}
